import { api } from '../client';

export interface Partner {
  _id: string;
  partner_id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  contact: {
    name?: string;
    email?: string;
    phone?: string;
  };
  whitelisted_ips: Array<{
    ip: string;
    description?: string;
    added_at: string;
  }>;
  authorized_tunnels: Array<{
    client_id: string;
    alias?: string;
    added_at: string;
  }>;
  integrations: Array<{
    _id: string;
    name: string;
    deployment_id?: string;
    status: string;
  }>;
  rate_limit: {
    requests_per_minute: number;
    requests_per_hour: number;
    requests_per_day: number;
  };
  usage: {
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    last_request?: string;
    last_ip?: string;
  };
  settings: {
    require_ip_whitelist: boolean;
    log_all_requests: boolean;
    webhook_retry_count: number;
    enabled_apis: {
      me: boolean;
      tunnels: boolean;
      tunnels_count: boolean;
      proxy: boolean;
      broadcast: boolean;
      call: boolean;
      call_wildcard: boolean;
    };
  };
  default_webhook?: {
    url?: string;
    enabled: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PartnerTunnel {
  _id: string;
  partner_id: string;
  client_id: string;
  alias?: string;
  client?: {
    client_id: string;
    client_name: string;
    deployment_name?: string;
    port?: number;
    status: string;
    last_seen?: string;
    hostname?: string;
  };
  createdAt: string;
}

export interface AvailableTunnel {
  client_id: string;
  client_name: string;
  deployment_name?: string;
  port?: number;
  status: string;
  hostname?: string;
  last_seen?: string;
}

export interface PartnerCredentials {
  api_key: string;
  partner_id: string;
  webhook_secret?: string;
}

export interface PartnerStats {
  total: number;
  active: number;
  byStatus: Record<string, number>;
  totalRequests: number;
}

export interface CreatePartnerData {
  name: string;
  description?: string;
  contact?: {
    name?: string;
    email?: string;
  };
  whitelisted_ips?: string[];
  default_webhook_url?: string;
  settings?: {
    require_ip_whitelist?: boolean;
  };
}

export const partnersApi = {
  list: (params?: { status?: string }) =>
    api.get<{ success: boolean; partners: Partner[] }>('/api/partners', params as Record<string, string>),

  get: (id: string) =>
    api.get<{ success: boolean; partner: Partner }>(`/api/partners/${id}`),

  create: (data: CreatePartnerData) =>
    api.post<{
      success: boolean;
      partner: Partner;
      credentials: PartnerCredentials;
      warning: string;
    }>('/api/partners', data),

  update: (id: string, data: Partial<Partner>) =>
    api.put<{ success: boolean; partner: Partner }>(`/api/partners/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/api/partners/${id}`),

  stats: () =>
    api.get<{ success: boolean; stats: PartnerStats }>('/api/partners/stats'),

  regenerateKey: (id: string) =>
    api.post<{ success: boolean; api_key: string }>(`/api/partners/${id}/regenerate-key`),

  getTunnels: (id: string, params?: { page?: number; limit?: number; search?: string }) =>
    api.get<{
      success: boolean;
      tunnels: PartnerTunnel[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/partners/${id}/tunnels`, params as Record<string, string | number>),

  addTunnel: (id: string, clientId: string, alias?: string) =>
    api.post<{ success: boolean; client_count: number }>(`/api/partners/${id}/tunnels`, { client_id: clientId, alias }),

  removeTunnel: (id: string, clientId: string) =>
    api.delete<{ success: boolean }>(`/api/partners/${id}/tunnels/${clientId}`),

  availableTunnels: (search?: string, limit?: number) =>
    api.get<{ success: boolean; tunnels: AvailableTunnel[]; count: number; total_available: number }>(
      '/api/partners/available-tunnels', { search, limit }
    ),

  updateApiAccess: (id: string, enabledApis: Partial<Partner['settings']['enabled_apis']>) =>
    api.put<{ success: boolean; partner: Partner }>(`/api/partners/${id}`, {
      settings: { enabled_apis: enabledApis },
    }),

  addWhitelistIp: (id: string, ip: string, description?: string) =>
    api.post<{ success: boolean }>(`/api/partners/${id}/whitelist`, { ip, description }),

  removeWhitelistIp: (id: string, ip: string) =>
    api.delete<{ success: boolean }>(`/api/partners/${id}/whitelist/${encodeURIComponent(ip)}`),
};
